import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useLanguage } from '../hooks/useLanguage';
import View from '../common/View';
import Text from '../common/Text';
import Button from '../common/Button';
import './ForgotPassword.css';

const ResetPassword: React.FC = () => {
  const { t } = useLanguage();
  const { onConfirmPasswordReset } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const c = searchParams.get('code') || searchParams.get('token');
    if (c) setCode(c);
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (password !== confirm) {
      setError(t('PASSWORDS_DO_NOT_MATCH') || 'Passwords do not match');
      return;
    }
    if (password.length < 8) {
      setError(
        t('PASSWORD_TOO_SHORT') || 'Password must be at least 8 characters',
      );
      return;
    }

    setLoading(true);
    try {
      await onConfirmPasswordReset({ code, newPassword: password });
      setSuccess(true);
      setTimeout(() => navigate('/signin'), 2000);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <View className="forgot-password">
      <View className="forgot-password-container">
        <View className="forgot-password-header">
          <Text variant="h1">{t('RESET_PASSWORD') || 'Reset password'}</Text>
          <Text variant="body">
            {success
              ? t('PASSWORD_RESET_SUCCESS') ||
                'Password updated. Redirecting to sign in…'
              : t('RESET_PASSWORD_SUBTITLE') ||
                'Enter the code from your email and a new password.'}
          </Text>
        </View>

        {!success && (
          <form onSubmit={handleSubmit} className="forgot-password-form">
            <View className="form-group">
              <Text variant="label">{t('RESET_CODE') || 'Reset code'}</Text>
              <input
                type="text"
                value={code}
                onChange={e => setCode(e.target.value)}
                className="form-input"
                placeholder="Paste the code from your email"
                required
                disabled={loading}
              />
            </View>
            <View className="form-group">
              <Text variant="label">{t('NEW_PASSWORD') || 'New password'}</Text>
              <input
                type="password"
                value={password}
                onChange={e => setPassword(e.target.value)}
                className="form-input"
                autoComplete="new-password"
                placeholder="At least 8 characters"
                required
                disabled={loading}
              />
            </View>
            <View className="form-group">
              <Text variant="label">
                {t('CONFIRM_PASSWORD') || 'Confirm password'}
              </Text>
              <input
                type="password"
                value={confirm}
                onChange={e => setConfirm(e.target.value)}
                className="form-input"
                autoComplete="new-password"
                required
                disabled={loading}
              />
            </View>

            {error && (
              <View className="signin-error">
                <Text variant="body">{error}</Text>
              </View>
            )}

            <Button
              type="submit"
              variant="primary"
              size="large"
              className="reset-button"
              disabled={loading}
            >
              {loading
                ? t('UPDATING') || 'Updating…'
                : t('RESET_PASSWORD') || 'Reset password'}
            </Button>
          </form>
        )}

        <View className="forgot-password-footer">
          <Text variant="body">
            <Link to="/signin">
              {t('BACK_TO_SIGN_IN') || 'Back to sign in'}
            </Link>
          </Text>
        </View>
      </View>
    </View>
  );
};

export default ResetPassword;
